import {
  useState,
  useRef,
  useEffect,
  useCallback,
  FormEvent,
  useMemo,
} from "react";
import {
  isFunction,
  useEventCallback,
  validate,
  setNestedObjectValues,
} from "./utils";
import setIn from "lodash/set";
import getIn from "lodash/get";
import { FormApi, FormProps } from "./types";
import { useFormContext } from "./provider";
import isEqual from "react-fast-compare";

type Fields = Record<string, any>;

export const useForm = <T extends Record<string, any>>({
  validationSchema,
  validateOnBlur = true,
  validateOnChange = true,
  enableReinitialize = false,
  onSubmit,
  ...rest
}: FormProps<T>): FormApi<any> => {
  const props = {
    validateOnChange,
    validateOnBlur,
    onSubmit,
    ...rest,
  };

  const initialValues = useRef(props.initialValues);

  const [formState, setFormState] = useState({
    isTouched: false,
    isSubmitting: false,
    errors: {} as { [key: string]: any },
    touched: {} as { [key: string]: any },
    submitCount: 0,
    values: props.initialValues,
    validationSchema,
  });

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const fieldRegistry = useRef<Fields>({});

  const executeSubmit = useEventCallback(() => {
    return onSubmit(formState.values);
  });

  const registerField = useCallback((name: string) => {
    fieldRegistry.current[name] = {};
  }, []);

  const unregisterField = useCallback((name: string) => {
    delete fieldRegistry.current[name];
  }, []);

  const resetForm = useCallback(() => {
    const values = initialValues.current;

    initialValues.current = values;

    setFormState((state) => ({
      ...state,
      isSubmitting: false,
      values,
      errors: {} as { [key: string]: any },
      touched: {} as { [key: string]: any },
      submitCount: 0,
    }));
  }, []);

  const runValidations = useCallback(
    (values: T): Promise<{ [key: string]: string }> => {
      return new Promise((resolve, reject) => {
        try {
          resolve(validate(values, validationSchema));
        } catch (e) {
          console.warn(
            `Warning: An unhandled error was caught from validationSchema`,
            e
          );
          reject(e);
        }
      });
    },
    [validationSchema]
  );

  const validateForm = useEventCallback((values = formState.values) => {
    return runValidations(values).then((errors) => {
      if (isMounted.current) {
        setFormState((state) => ({
          ...state,
          errors,
        }));
      }
      return errors;
    });
  });

  useEffect(() => {
    if (
      isMounted.current &&
      !isEqual(initialValues.current, props.initialValues)
    ) {
      if (enableReinitialize) {
        initialValues.current = props.initialValues;
        resetForm();
      }
    }
  }, [enableReinitialize, props.initialValues, resetForm]);

  /**
   * Update the value of a form field
   * @param field The name of the field to be updated
   * @param value The value to be updated
   * @param shouldValidate Wether the form should revalidate on change, defaults to true
   */
  const setFieldValue = useEventCallback(
    (field: string, value?: any, shouldValidate?: boolean) => {
      setFormState((state) => {
        return {
          ...state,
          values: setIn(state.values, field, value),
        };
      });

      const willValidate =
        shouldValidate === undefined ? validateOnChange : shouldValidate;

      return willValidate
        ? validateForm(setIn(formState.values, field, value))
        : Promise.resolve();
    }
  );
  /**
   * Mark a form field as being 'touched' i.e The user has interacted with the field
   * @param field The name of the field to be updated
   * @param value true if the field is touched, false if not
   * @param shouldValidate Wether the form should revalidate on blur(lose focus), defaults to true
   */
  const setFieldTouched = useEventCallback(
    (field: string, touched = true, shouldValidate?: boolean) => {
      setFormState((state) => ({
        ...state,
        touched: setIn(state.touched, field, touched),
      }));

      const willValidate =
        shouldValidate === undefined ? validateOnBlur : shouldValidate;
      return willValidate ? validateForm(formState.values) : Promise.resolve();
    }
  );

  /**
   * @param field The name of the field to be updated
   * @param error The error associated with the form field
   */
  const setFieldError = useEventCallback(
    (field: string, error: string | undefined) => {
      setFormState((state) => ({
        ...state,
        errors: setIn(state.errors, field, error),
      }));
    }
  );

  /**
   * Returns an object which contains the relevant state for a given field
   * @param name The name of the field
   *
   */
  const getFieldMeta = useCallback(
    (name: string) => {
      return {
        value: getIn(formState.values, name),
        error: getIn(formState.errors, name),
        touched: !!getIn(formState.touched, name),
      };
    },
    [formState.values, formState.errors, formState.touched]
  );

  /**
   * Returns an object which contains the relevant functions that are used to update a given field
   * @param name The name of the field
   */
  const getFieldHelpers = useCallback(
    (name: string) => {
      return {
        setValue: (value: any) => setFieldValue(name, value),
        setTouched: (value: boolean) => setFieldTouched(name, value),
        setError: (value: any) => setFieldError(name, value),
      };
    },
    [setFieldValue, setFieldTouched, setFieldError]
  );

  const submitForm = useEventCallback(() => {
    setFormState((state) => ({
      ...state,
      touched: setNestedObjectValues(formState.values, true),
      isSubmitting: true,
      submitCount: state.submitCount + 1,
    }));

    return validateForm().then((errors) => {
      const isInstanceOfError = errors instanceof Error;
      const isActuallyValid =
        !isInstanceOfError && Object.keys(errors).length === 0;

      if (isActuallyValid) {
        let promiseOrUndefined;
        try {
          promiseOrUndefined = executeSubmit();
          // Bail if it's sync, consumer is responsible for cleaning up
          // via setSubmitting(false)

          if (promiseOrUndefined === undefined) {
            setFormState((state) => ({
              ...state,
              isSubmitting: false,
            }));
            return;
          }
        } catch (error) {
          throw error;
        }

        return Promise.resolve(promiseOrUndefined)
          .then((result) => {
            if (isMounted.current) {
              setFormState((state) => ({
                ...state,
                isSubmitting: false,
              }));
            }

            return result;
          })
          .catch((errors) => {
            if (isMounted.current) {
              setFormState((state) => ({
                ...state,
                isSubmitting: false,
              }));
            }
            throw errors;
          });
      } else if (isMounted.current) {
        setFormState((state) => ({
          ...state,
          isSubmitting: false,
        }));
      }
    });
  });

  const handleSubmit = useEventCallback((e: FormEvent<HTMLFormElement>) => {
    if (e && e.preventDefault && isFunction(e.preventDefault)) {
      e.preventDefault();
    }

    if (e && e.stopPropagation && isFunction(e.stopPropagation)) {
      e.stopPropagation();
    }

    submitForm().catch((e) => {
      console.warn(
        `Warning: An unhandled error was caught from submitForm()`,
        e
      );
    });
  });

  const dirty = useMemo(
    () => !isEqual(initialValues.current, formState.values),
    [initialValues, formState.values]
  );

  const isValid = useMemo(
    () => formState.errors && Object.keys(formState.errors).length === 0,

    [formState.errors]
  );

  return {
    ...formState,
    handleSubmit,
    registerField,
    unregisterField,
    setFieldValue,
    setFieldTouched,
    getFieldMeta,
    getFieldHelpers,
    dirty,
    isValid,
  };
};

type UseFieldProps<T> = {
  meta: { value: T; touched: boolean; error: any };
  helpers: {
    setValue: (value: T) => void;
    setTouched: (touched: boolean) => void;
    setError: (error: any) => void;
  };
};

/**
 * A hook that returns a tuple of the field state and the functions to update a fields values
 * @param name The name of the field
 */
export const useField = <T = string>(name: string): UseFieldProps<T> => {
  const formContext = useFormContext();

  const { getFieldMeta, getFieldHelpers, registerField, unregisterField } =
    formContext;

  useEffect(() => {
    if (name) {
      registerField(name);
    }
    return () => {
      if (name) {
        unregisterField(name);
      }
    };
  }, [registerField, unregisterField, name]);

  return { meta: getFieldMeta(name), helpers: getFieldHelpers(name) };
};
